import { ConnectedPosition, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  Input,
  computed,
  effect,
  input,
  model,
  output,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { Observable, Subscription, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@infinitform/shared-ui';
import { InputComponent } from '../input/input.component';
import { fadeInOnEnterAnimation, slideInUpOnEnterAnimation } from 'angular-animations';

/** Space between an inline picker's button and its menu, as `.action-menu` keeps below its anchor. */
const INLINE_MENU_GAP_PX = 4;
/** Options a Page Up / Page Down moves in the boxed list. */
const PAGE_STEP = 10;
let nextSelectId = 0;
const SUBSCRIPTION_LOCK_REASON = 'Not available in your subscription';

/**
 * Two looks. The boxed field (default) is a form control with its value in a
 * box: Enter, Space or the arrow keys open its list and move into it; arrows,
 * Home/End, Page Up/Down and typing move the active option; Enter or Space
 * picks; Escape or Tab closes; closing returns focus to the field. The inline
 * picker (`size="text"`, or `type="custom-dropdown"` with the trigger text
 * projected) is the editor's text button with a chevron, the same as "Add part
 * group", and opens the shared `.action-menu` list: items take focus, arrows /
 * Home / End move, Enter or Space picks, Escape closes, and closing by Escape
 * or by picking returns focus to the button.
 */
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  imports: [CdkOverlayOrigin, CdkConnectedOverlay, FormsModule, NgTemplateOutlet, ButtonComponent, InputComponent],
  animations: [
    fadeInOnEnterAnimation({ duration: 150 }),
    slideInUpOnEnterAnimation({ duration: 150, translate: '10px' })
  ]
})
export class SelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  type = input<'custom-dropdown' | 'default'>('default');

  options = input<readonly unknown[] | Observable<unknown[]> | null>([]);
  asyncOptions = input<Observable<unknown[]> | null>(null);
  private _selected: unknown | unknown[] = null;
  @Input() disabled = false;
  multi = input(false);
  bindValue = input<string | undefined>(undefined);
  bindLabel = input<string | undefined>(undefined);
  bindBadge = input<string | undefined>(undefined);
  loading = model(false);
  placeholder = input('Select an option');
  /** Accessible name of the field; the trigger reads it with the current choice. */
  ariaLabel = input<string | null>(null);
  /** Id of an element that describes the field (e.g. its error message). */
  ariaDescribedBy = input<string | null>(null);
  searchable = input(false);
  searchPlaceholder = input('Search…');
  minSearchLength = input(0);
  searchDebounceTime = input(300, { alias: 'debounceTime' });
  clearable = input(false);
  change = output<unknown>();
  search = output<string>();
  size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'text'>('md');
  /** Text button + action menu instead of the boxed field. */
  readonly inline = computed(() => this.size() === 'text' || this.type() === 'custom-dropdown');

  @ViewChild('trigger') originElement!: ElementRef;
  @ViewChild('inlineTrigger', { read: ElementRef }) inlineTriggerElement?: ElementRef<HTMLElement>;
  @ViewChild('boxedTrigger') boxedTriggerElement?: ElementRef<HTMLButtonElement>;
  @ViewChild('listbox') listboxElement?: ElementRef<HTMLElement>;
  @ViewChild('searchInput', { static: false }) searchInput!: InputComponent;
  @ViewChild('optionsContainer') optionsContainer?: ElementRef<HTMLElement>;
  @ViewChildren('optionItem') optionItems?: QueryList<ElementRef<HTMLElement>>;
  get placeholderVisible(): boolean {
    if (this.multi()) {
      return !this._selected || (this._selected as unknown[]).length === 0;
    }
    return !this._selected;
  }

  private _isOpen: boolean = false;
  private currentValue: any = null;
  private optionsSubscription: Subscription | null = null;
  private _options: any[] = [];
  private _filteredOptions: any[] = [];
  private searchSubject = new Subject<string>();
  /** Which inline menu item takes focus when the menu opens. */
  private openFocus: 'initial' | 'last' = 'initial';
  searchTerm = '';
  /** The boxed list's active option (keyboard position or pointer), an index into availableOptions. */
  highlightedIndex = -1;
  /** The active option was reached with the keyboard, so it shows the focus ring. */
  keyboardActive = false;
  searchValue = '';
  readonly listboxId = `if-editor-select-${nextSelectId++}-listbox`;
  private typeahead = '';
  private typeaheadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Set up search subscription with debouncing
    this.searchSubject
      .pipe(debounceTime(this.searchDebounceTime()), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((searchTerm) => {
        this.performSearch(searchTerm);
      });

    // React to signal input changes (replaces ngOnChanges for options/asyncOptions)
    effect(() => {
      this.options();
      this.handleOptionsChange();
    });
    effect(() => {
      this.asyncOptions();
      this.handleAsyncOptionsChange();
    });
  }

  originWidth = 0;

  private updateOriginWidth(): void {
    this.originWidth = this.originElement?.nativeElement?.offsetWidth || 0;
  }

  get selected(): any | any[] {
    return this._selected;
  }

  get currentOptions(): Record<string, unknown>[] {
    return this.searchable() ? this._filteredOptions : this._options;
  }

  /**
   * Get available (unlocked) options
   */
  get availableOptions(): any[] {
    return this.currentOptions.filter((opt) => !opt?.['locked']);
  }

  /**
   * Get locked options (not available in subscription)
   */
  get lockedOptions(): any[] {
    return this.currentOptions.filter((opt) => opt?.['locked'] === true);
  }

  /**
   * Locked options grouped by their lock reason. Options may carry a
   * `lockedLabel` explaining WHY they are locked (e.g. the mixed load case
   * type gate); options without one fall back to the subscription text, so
   * existing subscription-locked consumers render unchanged.
   */
  get lockedOptionGroups(): { label: string; options: any[] }[] {
    const groups = new Map<string, any[]>();
    for (const option of this.lockedOptions) {
      const label = this.lockReason(option);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(option);
    }
    return Array.from(groups, ([label, options]) => ({ label, options }));
  }

  /** Why a locked option can't be picked (its `lockedLabel`, else the subscription). */
  private lockReason(option: Record<string, unknown>): string {
    return (option?.['lockedLabel'] as string | undefined) ?? SUBSCRIPTION_LOCK_REASON;
  }

  /** The inline menu's items: pickable ones first, then the locked ones by reason. */
  get menuOptions(): Record<string, unknown>[] {
    return [...this.availableOptions, ...this.lockedOptionGroups.flatMap((group) => group.options)];
  }

  optionLabel(option: unknown): string {
    const label = this.bindLabel();
    return String(label ? (option as Record<string, unknown>)[label] : option);
  }

  /** Quiet detail at an inline menu item's far edge: why it's locked, else its badge. */
  optionMeta(option: Record<string, unknown>): string | null {
    if (option?.['locked'] === true) return this.lockReason(option);
    const badge = this.bindBadge();
    return badge && option?.[badge] ? String(option[badge]) : null;
  }

  /** The inline button's name: the field with its current choice; a projected trigger names itself. */
  get inlineAriaLabel(): string | null {
    const name = this.ariaLabel();
    if (this.type() === 'custom-dropdown') return name;
    return name ? `${name}: ${this.selectedLabel}` : null;
  }

  /**
   * Check if there are any locked options to show the restricted group
   */
  get hasLockedOptions(): boolean {
    return this.lockedOptions.length > 0;
  }

  get isSearching(): boolean {
    return this.searchable() && this.searchTerm.length >= this.minSearchLength();
  }

  set selected(value: any | any[]) {
    this._selected = value;
    this.cdr.detectChanges();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.cdr.detectChanges();
  }

  get selectedLabel(): string {
    if (this.multi()) {
      if (!this.selected || (this.selected as unknown[]).length === 0) {
        return this.placeholder();
      }
      if ((this.selected as unknown[]).length === 1) {
        const label = this.bindLabel();
        return label ? (this.selected as Record<string, string>[])[0][label] : (this.selected as unknown[])[0] as string;
      }
      return `${(this.selected as unknown[]).length} items selected`;
    }

    const selected = this.selected;
    if (!selected) {
      return this.placeholder();
    }
    const label = this.bindLabel();
    return label ? (selected as Record<string, string>)[label] : selected as string;
  }

  get selectedBadge(): string | undefined {
    if (this.multi() || !this.selected || !this.bindBadge()) return undefined;
    return (this.selected as Record<string, string>)[this.bindBadge()!] || undefined;
  }

  // ControlValueAccessor implementation
  private onChange = (value: any) => {};
  private onTouched = () => {};

  writeValue(value: any): void {
    this.currentValue = value;

    if (value == null) {
      this.selected = this.multi() ? [] : null;
      return;
    }

    if (this.bindValue()) {
      if (this.multi()) {
        if (Array.isArray(value)) {
          this.selected = this._options.filter((opt: Record<string, unknown>) => value.includes(opt[this.bindValue()!]));
        } else {
          this.selected = [];
        }
      } else {
        const option = this._options.find((opt: Record<string, unknown>) => opt[this.bindValue()!] === value);
        this.selected = option || null;
      }
    } else {
      this.selected = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelected(option: Record<string, unknown>): boolean {
    if (this.multi()) {
      return this.selected && (this.selected as unknown[]).some((item: unknown) => item === option);
    }
    const selected = this.selected;
    if (!selected) return false;
    const bv = this.bindValue();
    if (bv) {
      if (bv !== 'value') {
        return (selected as Record<string, unknown>)[bv] === option[bv];
      }
      return (selected as Record<string, unknown>)['value'] === option['value'];
    }
    return option === selected;
  }

  /** Below the button from its left edge, else from its right edge (near the panel's right side), else above. */
  readonly inlineOverlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: INLINE_MENU_GAP_PX },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: INLINE_MENU_GAP_PX },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -INLINE_MENU_GAP_PX },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -INLINE_MENU_GAP_PX }
  ];

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom'
    }
  ];

  toggleDropdown(event?: MouseEvent) {
    // don't open/close if disabled
    if (this.disabled) return;

    if (!this.inline()) {
      if (this._isOpen) {
        this.closeBoxedList(true);
      } else {
        this.openBoxedList();
        // A click with no pointer behind it came from Enter or Space; a pointer
        // opening leaves nothing active until the pointer or a key picks one.
        if (event && event.detail === 0) this.keyboardActive = true;
        else if (!this.selected) this.highlightedIndex = -1;
      }
      return;
    }

    if (!this._isOpen) {
      this.updateOriginWidth();
    }
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.onDropdownOpen();
    } else {
      this.clearSearch();
    }
  }

  /** Opens the boxed list with the chosen option (else the first or last) active. */
  private openBoxedList(position: 'selected' | 'first' | 'last' = 'selected'): void {
    if (this.disabled || this._isOpen) return;
    this.updateOriginWidth();
    const options = this.availableOptions;
    const selectedIndex = options.findIndex((o) => this.isSelected(o));
    this.highlightedIndex =
      position === 'last' ? options.length - 1 : position === 'first' || selectedIndex < 0 ? (options.length ? 0 : -1) : selectedIndex;
    this.keyboardActive = false;
    this.isOpen = true;
    this.onDropdownOpen();
  }

  /** Closes the boxed list; `returnFocus` puts focus back on the field. */
  private closeBoxedList(returnFocus: boolean): void {
    if (!this._isOpen) return;
    this.keyboardActive = false;
    this.highlightedIndex = -1;
    this.isOpen = false;
    this.clearSearch();
    this.onTouched();
    if (returnFocus) this.boxedTriggerElement?.nativeElement.focus();
  }

  /** The boxed list is in the DOM: move focus into it (a searchable list focuses its search field itself). */
  onOverlayAttached(): void {
    if (this.inline()) return;
    setTimeout(() => {
      if (!this._isOpen || this.searchable()) return;
      this.listboxElement?.nativeElement.focus({ preventScroll: true });
      this.scrollActiveIntoView();
    });
  }

  onBoxedTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.openBoxedList(this.selected ? 'selected' : event.key === 'ArrowUp' ? 'last' : 'first');
      this.keyboardActive = true;
    }
  }

  /** Keys inside the open boxed list (the list, or its search field, has focus). */
  onListboxKeydown(event: KeyboardEvent): void {
    if (this.searchable()) {
      this.onSearchKeyDown(event);
      return;
    }
    const count = this.availableOptions.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(this.highlightedIndex < 0 ? 0 : Math.min(count - 1, this.highlightedIndex + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(this.highlightedIndex < 0 ? count - 1 : Math.max(0, this.highlightedIndex - 1));
        break;
      case 'Home':
        event.preventDefault();
        this.moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.moveActive(count - 1);
        break;
      case 'PageDown':
        event.preventDefault();
        this.moveActive(Math.min(count - 1, Math.max(0, this.highlightedIndex) + PAGE_STEP));
        break;
      case 'PageUp':
        event.preventDefault();
        this.moveActive(Math.max(0, this.highlightedIndex - PAGE_STEP));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (event.key === ' ' && this.typeahead) {
          this.typeaheadSearch(' ');
          break;
        }
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeBoxedList(true);
        break;
      case 'Tab':
        this.closeBoxedList(true);
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          this.typeaheadSearch(event.key);
        }
    }
  }

  /** The pointer moved over an option: it becomes active without the keyboard ring. */
  onOptionPointer(index: number): void {
    if (this.highlightedIndex === index && !this.keyboardActive) return;
    this.highlightedIndex = index;
    this.keyboardActive = false;
    this.cdr.detectChanges();
  }

  optionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  get activeDescendant(): string | null {
    return this._isOpen && this.highlightedIndex >= 0 && this.highlightedIndex < this.availableOptions.length
      ? this.optionId(this.highlightedIndex)
      : null;
  }

  private moveActive(index: number): void {
    if (index < 0 || index >= this.availableOptions.length) return;
    this.highlightedIndex = index;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    const id = this.activeDescendant;
    if (!id) return;
    this.listboxElement?.nativeElement.querySelector<HTMLElement>(`#${id}`)?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Jumps to the next option whose label starts with what was typed. */
  private typeaheadSearch(char: string): void {
    if (this.typeaheadTimer) clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);
    const options = this.availableOptions;
    const start = this.typeahead.length === 1 ? this.highlightedIndex + 1 : Math.max(0, this.highlightedIndex);
    for (let k = 0; k < options.length; k++) {
      const i = (start + k) % options.length;
      if (this.optionLabel(options[i]).toLowerCase().startsWith(this.typeahead)) {
        this.moveActive(i);
        return;
      }
    }
  }

  /**
   * Check if an individual option is disabled
   */
  isOptionDisabled(option: any): boolean {
    return option?.disabled === true || option?.locked === true;
  }

  selectOption(option: unknown) {
    if (this.disabled || this.isOptionDisabled(option)) return;

    if (this.multi()) {
      const selectedItems = (this.selected as unknown[]) || [];
      const index = selectedItems.findIndex((item: unknown) => item === option);
      if (index > -1) {
        selectedItems.splice(index, 1);
      } else {
        selectedItems.push(option);
      }
      this.selected = [...selectedItems];
      const bv = this.bindValue();
      const values = (this.selected as Record<string, unknown>[]).map((item) => (bv ? item[bv] : item));
      this.currentValue = values;
      this.onChange(values);
      this.change.emit(values);
    } else {
      const bv = this.bindValue();
      const value = bv ? (option as Record<string, unknown>)[bv] : option;

      this.selected = option;
      this.currentValue = value;
      this.onChange(value);
      this.onTouched();
      this.change.emit(value);
      if (this.inline()) {
        this.isOpen = false;
        this.focusInlineTrigger();
      } else {
        this.closeBoxedList(true);
      }
    }

    if (this.searchable()) {
      this.clearSearch();
    }
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    if (this.multi()) {
      this.selected = [];
      this.onChange([]);
      this.change.emit([]);
    } else if (this.clearable()) {
      this.selected = null;
      this.onChange(null);
      this.change.emit(null);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled'] && this.disabled) {
      // ensure overlay is closed when component becomes disabled
      if (this.isOpen) this.isOpen = false;
    }
  }

  ngOnDestroy() {
    if (this.typeaheadTimer) clearTimeout(this.typeaheadTimer);
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
  }

  private handleOptionsChange(): void {
    const opts = this.options();
    if (opts instanceof Observable) {
      this.subscribeToOptions(opts);
    } else if (opts) {
      this._options = [...opts];
      this._filteredOptions = [...this._options];
      this.updateSelectedFromCurrentValue();
    }
  }

  private handleAsyncOptionsChange(): void {
    const asyncOpts = this.asyncOptions();
    if (asyncOpts) {
      this.subscribeToOptions(asyncOpts);
    }
  }

  private subscribeToOptions(observable: Observable<any[]>) {
    // Unsubscribe from previous subscription
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }

    this.loading.set(true);
    this.optionsSubscription = observable.subscribe({
      next: (options) => {
        this._options = options;
        this._filteredOptions = [...this._options];
        this.loading.set(false);
        this.updateSelectedFromCurrentValue();
        this.cdr.detectChanges();
        if (this.isOpen) {
          this.queueScrollSelectedIntoView();
        }
      },
      error: (error) => {
        console.error('Error loading options:', error);
        this.loading.set(false);
        this._options = [];
        this._filteredOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  private updateSelectedFromCurrentValue(): void {
    const bv = this.bindValue();
    if (this.currentValue != null && bv && this._options.length > 0) {
      if (this.multi()) {
        if (Array.isArray(this.currentValue)) {
          this.selected = this._options.filter((opt: Record<string, unknown>) => this.currentValue.includes(opt[bv]));
        }
      } else {
        const option = this._options.find((opt: Record<string, unknown>) => opt[bv] === this.currentValue);
        this.selected = option || null;
      }
    }
  }

  /** Outside clicks close the menu; the origin's own click toggles it instead. */
  onOutsideClick(event: MouseEvent): void {
    const origin = this.originElement?.nativeElement as HTMLElement | undefined;
    if (origin && event.composedPath().includes(origin)) return;
    this.isOpen = false;
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          const items = this.menuItemElements();
          this.focusMenuItem(event.key === 'ArrowDown' ? 0 : items.length - 1);
        } else if (!this.disabled) {
          this.openFocus = event.key === 'ArrowDown' ? 'initial' : 'last';
          this.toggleDropdown();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.closeInlineMenu(true);
        }
        break;
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeInlineMenu(true);
      return;
    }
    if (event.key === 'Tab') {
      // Back on the button first, so Tab moves on from there.
      this.closeInlineMenu(true);
      return;
    }
    const items = this.menuItemElements();
    if (!items.length) return;
    const inSearch = (event.target as HTMLElement | null)?.tagName === 'INPUT';
    const current = items.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusMenuItem(current < 0 ? 0 : (current + 1) % items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusMenuItem(current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length);
        break;
      case 'Home':
        if (inSearch) return;
        event.preventDefault();
        this.focusMenuItem(0);
        break;
      case 'End':
        if (inSearch) return;
        event.preventDefault();
        this.focusMenuItem(items.length - 1);
        break;
    }
  }

  private closeInlineMenu(returnFocus: boolean): void {
    if (returnFocus) this.focusInlineTrigger();
    if (this.isOpen) {
      this.isOpen = false;
      this.clearSearch();
    }
  }

  /** The real `<button>` lives in the `app-button` shadow root. */
  private focusInlineTrigger(): void {
    this.inlineTriggerElement?.nativeElement.shadowRoot?.querySelector('button')?.focus();
  }

  private menuItemElements(): HTMLElement[] {
    return (this.optionItems?.toArray() ?? []).map((item) => item.nativeElement);
  }

  private focusMenuItem(index: number): void {
    this.menuItemElements()[index]?.focus();
  }

  /** The chosen item, else the first that can be picked. */
  private initialMenuIndex(): number {
    const options = this.menuOptions;
    const selected = options.findIndex((option) => this.isSelected(option));
    if (selected >= 0) return selected;
    const enabled = options.findIndex((option) => !this.isOptionDisabled(option));
    return enabled < 0 ? 0 : enabled;
  }

  // Search methods
  onSearchValueChange() {
    this.searchTerm = this.searchValue;
    this.highlightedIndex = -1;

    if (this.searchTerm.length >= this.minSearchLength()) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this._filteredOptions = [...this._options];
      this.cdr.detectChanges();
    }
  }

  onSearchKeyDown(event: KeyboardEvent) {
    if (!this.searchable()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeBoxedList(true);
        break;
      case 'Tab':
        this.closeBoxedList(true);
        break;
    }
  }

  private performSearch(searchTerm: string): void {
    if (!this.searchable()) return;

    this.search.emit(searchTerm);

    if (searchTerm.length < this.minSearchLength()) {
      this._filteredOptions = [...this._options];
    } else {
      this._filteredOptions = this._options.filter((option) => {
        const bl = this.bindLabel();
        const searchText = bl ? (option as Record<string, string>)[bl] : option.toString();
        return searchText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    this.highlightedIndex = -1;
    this.cdr.detectChanges();
  }

  private highlightNext() {
    const count = this.availableOptions.length;
    if (count === 0) return;
    this.highlightedIndex = (this.highlightedIndex + 1) % count;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private highlightPrevious() {
    const count = this.availableOptions.length;
    if (count === 0) return;
    this.highlightedIndex = this.highlightedIndex <= 0 ? count - 1 : this.highlightedIndex - 1;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private selectHighlighted() {
    const options = this.availableOptions;
    if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
      this.selectOption(options[this.highlightedIndex]);
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchValue = '';
    this.highlightedIndex = -1;
    this._filteredOptions = [...this._options];
    this.cdr.detectChanges();
  }

  isHighlighted(option: unknown): boolean {
    const index = this.availableOptions.findIndex((opt) => opt === option);
    return index >= 0 && index === this.highlightedIndex;
  }

  onDropdownOpen() {
    const focus = this.openFocus;
    this.openFocus = 'initial';
    if (this.inline() && !this.searchable()) {
      setTimeout(() => {
        if (!this.isOpen) return;
        const items = this.menuItemElements();
        this.focusMenuItem(focus === 'last' ? items.length - 1 : this.initialMenuIndex());
      }, 0);
    }

    if (this.searchable() && this.searchInput) {
      // Focus search input when dropdown opens
      setTimeout(() => {
        const inputElement = (this.searchInput as any).elementRef?.nativeElement?.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }

    this.queueScrollSelectedIntoView();
  }

  private queueScrollSelectedIntoView() {
    // Defer until the view updates to ensure option elements are present
    setTimeout(() => {
      if (!this.isOpen) return;
      this.scrollSelectedIntoView();
    }, 0);
  }

  private scrollSelectedIntoView() {
    const container = this.optionsContainer?.nativeElement;
    const optionElements = this.optionItems?.toArray() ?? [];
    if (!container || optionElements.length === 0) return;

    const targetIndex = this.getFirstSelectedIndex();
    if (targetIndex < 0 || targetIndex >= optionElements.length) return;

    const targetElement = optionElements[targetIndex].nativeElement;
    if (!targetElement) return;

    if (typeof targetElement.scrollIntoView === 'function') {
      targetElement.scrollIntoView({ block: 'nearest' });
    } else {
      const { offsetTop, offsetHeight } = targetElement;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const offsetBottom = offsetTop + offsetHeight;

      if (offsetTop < scrollTop) {
        container.scrollTop = offsetTop;
      } else if (offsetBottom > scrollTop + containerHeight) {
        container.scrollTop = offsetBottom - containerHeight;
      }
    }
  }

  private getFirstSelectedIndex(): number {
    const options = this.currentOptions;
    if (!options || options.length === 0) return -1;

    if (this.multi()) {
      return options.findIndex((option) => this.isSelected(option));
    }

    const selected = this.selected;
    if (!selected) return -1;

    if (this.bindValue()) {
      return options.findIndex((option) => option['value'] === (selected as Record<string, unknown>)['value']);
    }

    return options.findIndex((option) => option === selected);
  }
}
